/**
 * Run health as TEXT — Himaad's Section 5 (`Schedules / List` 589:9933).
 *
 * Replaces `RunHealthSparkline`, and the design is right that text is better
 * here: a sparkline can say "one of these was bad" and cannot say WHICH or WHEN,
 * which is the only thing a person looking at a failed month-end wants.
 *
 * ## The denominator is measured, not the mock's "10"
 *
 * The mock reads **"9 of 10 ran"**. That string is **unreachable**, and shipping
 * it would state something false about the user's own data:
 *
 *   - `pruneScheduleResults` deletes everything past `retain_count` immediately
 *     after each firing, and the default `retain_count` is **5** (the editor's
 *     highest option is 24);
 *   - `listRunsForSchedules` additionally excludes already-expired rows.
 *
 * So on a default schedule the history available to this cell can only ever be
 * five runs, and a hardcoded "of 10" would claim five runs that do not exist. The
 * denominator here is the number of runs that have actually FINISHED, and the
 * COLUMN HEADER is driven by the same measurement for the same reason (see
 * `ScheduleRow`).
 *
 * If a stable 10-run window is wanted, that is a retention-floor change: raise
 * the minimum `retain_count`, or keep a slim run-audit row that survives result
 * pruning. It is a backend decision, not a copy one.
 *
 * ## WCAG 1.4.1
 *
 * This text is now the *only* carrier of run health, so it must not depend on
 * colour. The failure clause names the date in words ("1 failed Jul 30"); red is
 * an emphasis on information already stated, not the information itself. That is
 * why the sparkline could only be retired in the same change that added this.
 *
 * ## One thing genuinely lost
 *
 * The sparkline encoded each run's DURATION as bar height, so a schedule getting
 * steadily slower was visible at a glance. Nothing in the text form replaces
 * that, and the Last-run cell shows a duration only for the newest run (spec 182:
 * the newest of ANY kind and state — it reads this component's own `runs`).
 * Recorded rather than glossed: it is a real regression in one dimension,
 * accepted because "which run failed, and when" matters more.
 */
import { summarizeRuns, failureNote } from './scheduleFormat'

/*
 * `historyWithheld` distinguishes "there are no runs" from "we were not shown the
 * runs" (QA, 2026-09-03).
 *
 * The list route forces `runs` to `[]` for a schedule the caller does not own —
 * deliberately, because a colleague's result rows carry THEIR row-level data scope
 * (the CC-50231 rule). This component then read that empty array as a fact and
 * rendered **"No runs yet"**, a claim about a colleague's schedule whose history
 * the reader was denied — and the Last-run cell beside it disproved it in the same
 * row by printing a real timestamp.
 *
 * Exactly the empty-vs-failed-read family this codebase keeps getting caught by,
 * one level along: here it is empty-vs-WITHHELD.
 */
const RunHealthText = ({ runs, paused = false, historyWithheld = false, onViewHistory }) => {
  if (historyWithheld) {
    return <span className="text-gray-500">Not shown for others&apos; schedules</span>
  }

  const list = runs || []

  /*
   * Paused and never-run are DIFFERENT from healthy, and both differ from each
   * other. An em-dash for a paused schedule matches the design; "0 of 0 ran"
   * would read as a failure, and a bare blank reads as a rendering bug.
   */
  /*
   * gray-500, not gray-400 — spec 118 §D1. The em-dash is TEXT, so SC 1.4.3's
   * 4.5:1 applies to it. At the hexes tailwindcss 4.2.1 actually emits (its palette
   * is oklch, so the v3 hexes these comments first carried are in no build output):
   * gray-400 #99a1af is 2.602:1 on white / 2.490:1 on #f9fafb, and gray-500 #6a7282
   * is 4.836 / 4.628. A deviation from the mock, which specifies the lighter grey.
   */
  if (paused) return <span className="text-gray-500">—</span>

  /*
   * The denominator is `total` — the count of FINISHED runs — not `list.length`.
   *
   * `summarizeRuns` filters to terminal states first, and that distinction is
   * load-bearing rather than tidy: a schedule with one run still in flight would
   * otherwise read "4 of 5 ran", which says a run FAILED when it is simply not
   * finished. On a month-end pack that is the difference between "check this" and
   * "wait a minute".
   */
  const { total, failureCount } = summarizeRuns(list)
  if (total === 0) {
    // Runs exist but none has finished. Say that, rather than "0 of 0 ran".
    return <span className="text-gray-500">{list.length ? 'First run in progress' : 'No runs yet'}</span>
  }

  const ok = total - failureCount
  const note = failureNote(list)

  return (
    <span className="inline-flex flex-col items-start gap-px">
      <span className="tabular-nums text-[12.5px] text-gray-800">
        {`${ok} of ${total} ran`}
      </span>
      {note && (
        /*
         * A BUTTON, because it is the natural drill-down into run history — the
         * thing a reader wants the instant they see a failure. It was already
         * clickable beside the sparkline; keeping it clickable is what stops the
         * text form being strictly less useful than the graphic it replaces.
         */
        onViewHistory ? (
          <button
            type="button"
            onClick={onViewHistory}
            className="rounded text-[11.5px] font-medium text-rc-danger-text underline decoration-1 underline-offset-2 hover:no-underline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-current"
          >
            {note}
          </button>
        ) : (
          <span className="text-[11.5px] font-medium text-rc-danger-text">{note}</span>
        )
      )}
    </span>
  )
}

export default RunHealthText
